import type { InputHTMLAttributes } from "react"
import { forwardRef, useState } from "react"

import {
  Autocomplete,
  Box,
  Checkbox,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  type BoxProps,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { uniqBy } from "lodash"
import { Controller, useFormContext } from "react-hook-form"

import { Button, IconButton, SvgIcon, type FC, type iconKeys } from ".."

type Facet = {
  label: string
  value: string
  experience?: string
}

type ArticleSearchFormProps = {
  destinations?: Facet[]
  countries?: Facet[]
  experiences?: Facet[]
  onSearch?: () => void
  onApplyFilter: (value: any, type: string) => void
}

const BlogSearch: FC<ArticleSearchFormProps> = ({
  destinations = [],
  countries = [],
  experiences = [],
  onSearch,
  onApplyFilter,
}) => {
  const [openDialog, setOpenDialog] = useState(false)

  return (
    <Box>
      <Box display={{ xs: "none", md: "block" }}>
        <SearchForm
          destinations={destinations}
          countries={countries}
          experiences={experiences}
          onSearch={onSearch as () => void}
          onApplyFilter={onApplyFilter as any}
        />
      </Box>
      <Box display={{ md: "none" }}>
        <Button
          variant="base"
          onClick={() => setOpenDialog(true)}
          sx={(theme) => ({
            border: `1px solid ${theme.palette["neutral.200"].main}`,
            borderRadius: 1.5,
            justifyContent: "flex-start",
            width: { xs: "100%" },
            height: { xs: 54 },
            px: { xs: 5 },
          })}
          startIcon={<SvgIcon variant="sort" size={16} />}
          endIcon={<SvgIcon variant="bullet" size={8} sx={{ transform: "rotate(90deg)" }} />}
        >
          <Typography variant="link.xs" ml={3} mr="auto">
            Filter By
          </Typography>
        </Button>
      </Box>
      <SearchDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        ariaLabel="Filter Trip Itineraries"
      />
    </Box>
  )
}
export default BlogSearch

const SearchForm: FC<ArticleSearchFormProps> = ({
  destinations = [],
  countries = [],
  experiences = [],
  onSearch,
  onApplyFilter,
}) => {
  const { control, getValues } = useFormContext()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  return (
    <Box
      component="form"
      display={{ xs: "flex", md: "grid" }}
      gridTemplateColumns={{
        md: "repeat(3, minmax(0, 1fr)) auto",
      }}
      rowGap={2}
      sx={(theme) => ({
        "bgcolor": theme.palette["neutral.100"].main,
        "borderRadius": 1.5,
        "boxShadow": { md: `0px 2px 10px ${alpha(theme.palette["neutral.900"].main, 0.08)}` },
        "position": "relative",

        "& > *:first-of-type": {
          borderRadius: { md: theme.spacing(1.5, 0, 0, 1.5) },

          input: {
            borderRadius: { md: theme.spacing(1.5, 0, 0, 1.5) },
          },
        },

        "& > *:nth-of-type(n+2):nth-of-type(-n+3)": {
          borderLeft: { md: `1px solid ${theme.palette["neutral.200"].main}` },
        },

        [theme.breakpoints.down("md")]: {
          flexDirection: "column",
          height: "100%",
        },
      })}
    >
      <Box>
        <Controller
          name="destination"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              disablePortal={isMobile}
              openOnFocus
              options={destinations}
              value={value}
              onChange={(_, newValue: string | null) => {
                onChange(newValue)
                onApplyFilter(newValue, "destination")
              }}
              renderInput={(params) => (
                <SearchInputField
                  ref={params.InputProps.ref}
                  placeholder="Destinations"
                  aria-label="Destinations"
                  icon="pinMap2"
                  inputProps={params.inputProps}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 324,
                },
                sx: {
                  p: 2,
                },
              }}
              renderOption={(props, option, { selected }) => (
                <ListItem
                  disablePadding
                  selected={selected}
                  sx={(theme) => ({
                    "border": `1px solid transparent`,
                    "minHeight": "48px!important",
                    "py": 3.5,
                    "px": 4,

                    "&.Mui-focused, &.Mui-selected": {
                      bgcolor: `${theme.palette["secondary.200"].main}!important`,
                      borderRadius: 1,
                      color: `${theme.palette["secondary.800"].main}!important`,
                    },
                    "&.Mui-selected": {
                      borderColor: theme.palette["secondary.300"].main,
                    },
                  })}
                  {...props}
                >
                  <ListItemText primaryTypographyProps={{ variant: "link.xs" }}>
                    {option.label}
                  </ListItemText>
                </ListItem>
              )}
              slotProps={{
                popper: {
                  sx: {
                    minWidth: { sm: "276px!important" },
                  },
                },
                paper: {
                  sx: {
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
                    boxShadow: (theme) =>
                      `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
                    fontSize: { xs: 10, md: 12 },
                    my: 1,
                  },
                },
              }}
            />
          )}
        />
      </Box>
      <Box>
        <Controller
          name="country"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              value={value}
              onChange={(_, newValue) => {
                onChange(newValue)
                onApplyFilter(newValue, "country")
              }}
              disablePortal={isMobile}
              openOnFocus
              options={countries}
              getOptionDisabled={(option) => option.label === "Please select a destination first."}
              renderInput={(params) => (
                <SearchInputField
                  ref={params.InputProps.ref}
                  placeholder="Countries"
                  aria-label="Countries"
                  icon="globe"
                  inputProps={params.inputProps}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <ListItem
                  disablePadding
                  selected={selected}
                  sx={(theme) => ({
                    "border": `1px solid transparent`,
                    "minHeight": "48px!important",
                    "py": 3.5,
                    "px": 4,

                    "&.Mui-focused, &.Mui-selected": {
                      bgcolor: `${theme.palette["secondary.200"].main}!important`,
                      borderRadius: 1,
                      color: `${theme.palette["secondary.800"].main}!important`,
                    },
                    "&.Mui-selected": {
                      borderColor: theme.palette["secondary.300"].main,
                    },
                  })}
                  {...props}
                >
                  <ListItemText primaryTypographyProps={{ variant: "link.xs" }}>
                    {option.label}
                  </ListItemText>
                </ListItem>
              )}
              ListboxProps={{
                style: {
                  maxHeight: 324,
                },
                sx: {
                  p: 2,
                },
              }}
              slotProps={{
                popper: {
                  sx: {
                    minWidth: { sm: "276px!important" },
                  },
                },
                paper: {
                  sx: {
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
                    boxShadow: (theme) =>
                      `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
                    fontSize: { xs: 10, md: 12 },
                    my: 1,
                  },
                },
              }}
            />
          )}
        />
      </Box>
      <Box>
        <Controller
          name="experiences"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              openOnFocus
              disableCloseOnSelect
              disablePortal={isMobile}
              multiple
              options={experiences}
              value={value}
              onChange={(_, data) => {
                onChange(uniqBy(data, "label"))
                onApplyFilter(data, "experiences")
              }}
              groupBy={(option) => option.experience as string}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label &&
                getValues("experiences").some((x: any) => x.label === option.label)
              }
              renderInput={(params) => (
                <SearchInputField
                  ref={params.InputProps.ref}
                  placeholder="Experiences"
                  aria-label="Experiences"
                  icon="palmTree"
                  inputProps={{
                    ...params.inputProps,
                    value: getValues("experiences")
                      .map(({ label }: any) => label)
                      .join(", "),
                  }}
                />
              )}
              renderGroup={({ children, group }) => (
                <List key={group}>
                  <ListItem
                    disablePadding
                    sx={(theme) => ({
                      bgcolor: theme.palette["neutral.100"].main,
                      position: "sticky",
                      height: "48px!important",
                      px: 4,
                      py: 3.5,
                      top: 0,
                      zIndex: 1,
                    })}
                  >
                    <ListItemText primaryTypographyProps={{ variant: "body.sm", fontWeight: 500 }}>
                      {group}
                    </ListItemText>
                  </ListItem>
                  {children}
                </List>
              )}
              ListboxProps={{
                style: {
                  maxHeight: 324,
                },
                sx: {
                  "columnGap": 5,
                  "display": "grid",
                  "gridTemplateColumns": { md: "repeat(2, minmax(0, 1fr))" },
                  "py": 0,
                  "px": 2,
                  "position": "relative",
                  "overflow": "auto",
                  "width": "100%",

                  "&::before": {
                    content: "''",
                    position: "fixed",
                    display: { xs: "none", md: "block" },
                    top: 16,
                    bottom: 16,
                    width: "1px",
                    bgcolor: (theme) => theme.palette["neutral.200"].main,
                    left: "calc(50% - 5px)",
                    transform: "translateX(-50%)",
                  },
                },
              }}
              renderOption={(props, option, { selected }) => (
                <ListItem
                  disablePadding
                  sx={(theme) => ({
                    "px": 4,
                    "display": "flex",
                    "columnGap": 3,
                    "minHeight": "48px!important",
                    "borderRadius": 1.5,

                    "&[aria-selected='true']": {
                      bgcolor: "transparent!important",
                    },

                    "&.Mui-focused, &.Mui-selected": {
                      bgcolor: `${theme.palette["secondary.200"].main}!important`,
                      borderRadius: 1,
                      color: `${theme.palette["secondary.800"].main}!important`,
                    },
                    "&.Mui-selected": {
                      borderColor: theme.palette["secondary.300"].main,
                    },
                  })}
                  {...props}
                >
                  <ListItemIcon sx={{ minWidth: "unset" }}>
                    <Checkbox
                      icon={
                        <SvgIcon
                          variant="checkboxEmpty"
                          sx={{ fill: (theme) => alpha(theme.palette["neutral.800"].main, 0.12) }}
                        />
                      }
                      checkedIcon={
                        <SvgIcon
                          variant="checkbox"
                          sx={{ fill: (theme) => theme.palette["secondary.800"].main }}
                        />
                      }
                      checked={selected}
                      sx={{ color: (theme) => theme.palette["secondary.200"].main, p: 0 }}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: "body.sm" }}>
                    {option.label}
                  </ListItemText>
                </ListItem>
              )}
              slotProps={{
                popper: {
                  sx: {
                    width: { md: "600px!important" },
                  },
                },
                paper: {
                  sx: {
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
                    boxShadow: (theme) =>
                      `0 0 8px ${alpha(theme.palette["neutral.900"].main, 0.06)}`,
                    fontSize: { xs: 10, md: 12 },
                    my: 1,
                  },
                },
              }}
            />
          )}
        />
      </Box>
      <Box mt="auto" display={{ md: "none" }}>
        <Button
          variant="default"
          type="submit"
          endIcon={<SvgIcon variant="arrow" size={16} sx={{ transform: "rotate(180deg)" }} />}
          sx={{
            width: "100%",
            display: "flex",
            px: { xs: 5 },
            py: { xs: 3.5 },
            justifyContent: "space-between",
          }}
        >
          Search Results
        </Button>
      </Box>
    </Box>
  )
}

type InputFieldProps = {
  inputProps: InputHTMLAttributes<HTMLInputElement>
  icon: string
} & BoxProps<"input">

const SearchInputField = forwardRef<any, InputFieldProps>(({ inputProps, icon, ...props }, ref) => (
  <Box ref={ref} position="relative" sx={{ isolation: "isolate" }}>
    <Box
      component="input"
      type="text"
      sx={(theme) => ({
        ...theme.typography["body.sm"],
        "bgcolor": "transparent",
        "border": { xs: `1px solid ${theme.palette["neutral.200"].main}`, md: "0" },
        "borderRadius": { xs: 1.5, md: 0 },
        "display": "block",
        "fontWeight": 500,
        "height": 68,
        "p": 4,
        "pl": 11.5,
        "width": "100%",
        "&::placeholder": {
          color: theme.palette["neutral.700"].main,
          fontWeight: 400,
          opacity: 0.7,
        },

        "&:focus": {
          "bgcolor": theme.palette["secondary.200"].main,
          "color": theme.palette["secondary.800"].main,
          "outline": "none",

          "&::placeholder": {
            color: theme.palette["secondary.800"].main,
          },

          "& + svg": {
            color: theme.palette["secondary.800"].main,
          },
        },
      })}
      {...props}
      {...inputProps}
    />
    <SvgIcon
      variant={icon as iconKeys}
      size={16}
      sx={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", zIndex: 1 }}
    />
  </Box>
))
SearchInputField.displayName = "SearchInputField"

type DrawerProps = {
  open: boolean
  onClose: () => void
  ariaLabel?: string
  destinations?: Facet[]
  countries?: Facet[]
  experiences?: Facet[]
  onSearch?: (test: any) => void
  onApplyFilter?: (value: any, type: string) => void
}

const SearchDialog: FC<DrawerProps> = ({
  open,
  onClose,
  ariaLabel,
  destinations,
  countries,
  experiences,
  onApplyFilter,
}) => {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      aria-label={ariaLabel}
      anchor="bottom"
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          flexDirection: "row",
          alignItems: "stretch",
          flexWrap: "wrap",
          display: { md: "grid" },
          gridTemplateColumns: { md: "clamp(350px, 35.15625%, 450px) 1fr" },
          borderRadius: (theme) => theme.spacing(1.5, 1.5, 0, 0),
          mx: 3,
          p: 5,
          minHeight: 728,
          zIndex: 4100,
        },
      }}
      sx={{
        zIndex: 4000,
      }}
    >
      <Stack flex="1 1 100%">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4.5}>
          <Typography variant="link.xs" sx={{ opacity: 0.7 }}>
            {ariaLabel}
          </Typography>
          <IconButton
            variant="secondary"
            onClick={onClose}
            icon={<SvgIcon variant="x" size={14} />}
            sx={{
              width: { xs: 44 },
              height: { xs: 44 },
            }}
          />
        </Stack>
        <Box flex="1 1 100%">
          <SearchForm
            destinations={destinations}
            countries={countries}
            experiences={experiences}
            onSearch={onClose}
            onApplyFilter={onApplyFilter as any}
          />
        </Box>
      </Stack>
    </Drawer>
  )
}
